import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { YearlyMarketVolumeChartProps } from './YearlyMarketVolumeChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 350px;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

export const YearlyMarketVolumeChart: React.FC<
  YearlyMarketVolumeChartProps
> = ({
  data,
  loading = false,
  error = null,
  title = 'Yearly Market Volume',
  height = 400,
}) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!loading && !error && data && data.length > 0 && chartRef.current) {
      initializeChart()
    }

    return () => {
      chartInstance.current?.dispose()
    }
  }, [data, loading, error])

  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const formatCurrency = (value: number) => {
    if (value >= 1e9) return `CHF ${(value / 1e9).toFixed(2)}B`
    if (value >= 1e6) return `CHF ${(value / 1e6).toFixed(0)}M`
    if (value >= 1e3) return `CHF ${(value / 1e3).toFixed(0)}K`
    return `CHF ${value.toFixed(0)}`
  }

  const initializeChart = () => {
    if (!chartRef.current || !data) return

    if (chartInstance.current) {
      chartInstance.current.dispose()
    }

    chartInstance.current = echarts.init(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort(
      (a, b) => parseInt(a.year) - parseInt(b.year)
    )

    const option: echarts.EChartsOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = params[0]
          return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 4px;">Year ${param.axisValue}</div>
              <div>Total Market Volume: ${formatCurrency(param.value)}</div>
            </div>
          `
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '20%',
        bottom: '15%',
      },
      xAxis: {
        type: 'category',
        data: sortedData.map((d) => d.year),
        name: 'Year',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
        },
        axisLabel: {
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Total Market Volume (CHF)',
        nameLocation: 'middle',
        nameGap: 60,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 600,
        },
        axisLabel: {
          formatter: (value: number) => formatCurrency(value),
        },
      },
      series: [
        {
          type: 'bar',
          data: sortedData.map((d) => d.volume),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#5470c6' },
              { offset: 1, color: '#91cc75' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#4460b6' },
                { offset: 1, color: '#81bc65' },
              ]),
            },
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => formatCurrency(params.value),
            fontSize: 10,
            color: '#666',
          },
          barWidth: '60%',
        },
      ],
    }

    chartInstance.current.setOption(option)
  }

  if (loading) {
    return (
      <LoadingContainer justify="center" align="center">
        <Loading size="large" />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <ErrorContainer justify="center" align="center">
        {error}
      </ErrorContainer>
    )
  }

  if (!data || data.length === 0) {
    return (
      <ErrorContainer justify="center" align="center">
        No yearly market volume data available
      </ErrorContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartWrapper $height={height} ref={chartRef} />
    </ChartContainer>
  )
}
